import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle, Loader2, Package, CreditCard, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const paymentMethod = searchParams.get('method');
  const transferType = searchParams.get('type');
  const paypalToken = searchParams.get('token'); // PayPal returns this after approval
  const bankDetails = location.state?.bankDetails;

  useEffect(() => {
    const checkPaymentStatus = async () => {
      // PayPal: capture payment after user approval
      if (paypalToken && paymentMethod === 'paypal') {
        try {
          const captureRes = await axios.post(`${API_URL}/api/payments/paypal/capture/${paypalToken}`);
          if (captureRes.data.success) {
            setPaymentStatus({
              success: true,
              method: 'paypal'
            });
          } else {
            setError(language === 'it' ? 'Errore nella cattura del pagamento PayPal' : 'PayPal capture error');
          }
        } catch (err) {
          setError(language === 'it' ? 'Errore nel pagamento PayPal' : 'PayPal payment error');
        } finally {
          setLoading(false);
        }
        return;
      }

      // For Stripe payments, poll the status
      if (sessionId && paymentMethod !== 'cod' && paymentMethod !== 'bank_transfer') {
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 2000; // 2 seconds

        const poll = async () => {
          if (attempts >= maxAttempts) {
            setError(language === 'it' 
              ? 'Timeout nella verifica del pagamento. Controlla la tua email per la conferma.' 
              : 'Payment verification timeout. Check your email for confirmation.');
            setLoading(false);
            return;
          }

          try {
            const response = await axios.get(`${API_URL}/api/payments/stripe/status/${sessionId}`);
            
            if (response.data.payment_status === 'paid') {
              setPaymentStatus({
                success: true,
                method: 'stripe',
                amount: response.data.amount_total / 100, // Convert from cents
                currency: response.data.currency
              });
              setLoading(false);
            } else if (response.data.status === 'expired') {
              setError(language === 'it' 
                ? 'Sessione di pagamento scaduta. Riprova.' 
                : 'Payment session expired. Please try again.');
              setLoading(false);
            } else {
              // Continue polling
              attempts++;
              setTimeout(poll, pollInterval);
            }
          } catch (err) {
            setError(language === 'it' 
              ? 'Errore nella verifica del pagamento' 
              : 'Error checking payment status');
            setLoading(false);
          }
        };

        poll();
      } else {
        // For COD and Bank Transfer, no polling needed
        setPaymentStatus({
          success: true,
          method: paymentMethod,
          transferType: transferType
        });
        setLoading(false);
      }
    };

    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paymentMethod, transferType, language]);

  // Google Ads Conversion tracking (AW-18098930989/FcwgCOO-x7EcEK2Kn7ZD)
  // Fires once per order_id; guarded via sessionStorage to avoid re-fire on refresh
  useEffect(() => {
    if (!paymentStatus?.success || !orderId) return;
    const fireKey = `gads_conv_fired_${orderId}`;
    if (sessionStorage.getItem(fireKey) === '1') return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    const conversionPayload = {
      send_to: 'AW-18098930989/FcwgCOO-x7EcEK2Kn7ZD',
      transaction_id: orderId,
    };
    if (paymentStatus.amount && paymentStatus.amount > 0) {
      conversionPayload.value = paymentStatus.amount;
      conversionPayload.currency = (paymentStatus.currency || 'eur').toUpperCase();
    }
    window.gtag('event', 'conversion', conversionPayload);
    sessionStorage.setItem(fireKey, '1');
  }, [paymentStatus, orderId]);

  if (loading) {
    return (
      <div className="dark-container pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin mx-auto mb-4 text-primary" />
          <p className="text-lg">
            {language === 'it' ? 'Verifica del pagamento in corso...' : 'Verifying payment...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dark-container pt-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <AlertCircle size={64} className="mx-auto mb-6 text-red-500" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            {language === 'it' ? 'Errore' : 'Error'}
          </h1>
          <p className="text-lg text-white/80 mb-8">{error}</p>
          <button onClick={() => navigate('/products')} className="btn-primary">
            {language === 'it' ? 'Torna ai Prodotti' : 'Back to Products'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dark-container pt-20 min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-20">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <CheckCircle size={80} className="mx-auto mb-6 text-primary animate-pulse" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            {language === 'it' ? 'Ordine Confermato!' : 'Order Confirmed!'}
          </h1>
          <p className="text-lg text-white/80">
            {language === 'it' 
              ? 'Grazie per il tuo acquisto. Riceverai una email di conferma a breve.' 
              : 'Thank you for your purchase. You will receive a confirmation email shortly.'}
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-white/5 rounded-lg p-8 border border-white/10 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Package size={24} className="text-primary" />
            <h2 className="text-2xl font-bold">
              {language === 'it' ? 'Dettagli Ordine' : 'Order Details'}
            </h2>
          </div>
          
          {orderId && (
            <div className="mb-4 pb-4 border-b border-white/10">
              <p className="text-sm text-white/60 mb-1">
                {language === 'it' ? 'Numero Ordine' : 'Order Number'}
              </p>
              <p className="text-xl font-mono font-bold text-primary">{orderId}</p>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CreditCard size={20} className="text-white/60" />
              <div>
                <p className="text-sm text-white/60">
                  {language === 'it' ? 'Metodo di Pagamento' : 'Payment Method'}
                </p>
                <p className="font-semibold">
                  {paymentMethod === 'stripe' && (language === 'it' ? 'Carta di Credito' : 'Credit Card')}
                  {paymentMethod === 'paypal' && 'PayPal'}
                  {paymentMethod === 'cod' && (language === 'it' ? 'Contrassegno' : 'Cash on Delivery')}
                  {paymentMethod === 'bank_transfer' && (language === 'it' ? 'Bonifico Bancario' : 'Bank Transfer')}
                </p>
              </div>
            </div>

            {paymentMethod === 'cod' && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-4 mt-4">
                <p className="text-sm">
                  {language === 'it' 
                    ? '💰 Pagherai alla consegna (€4.00 di supplemento incluso)' 
                    : '💰 You will pay on delivery (€4.00 fee included)'}
                </p>
              </div>
            )}

            {paymentMethod === 'bank_transfer' && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4 mt-4">
                <h3 className="font-semibold mb-2">
                  {language === 'it' ? 'Coordinate Bancarie' : 'Bank Details'}
                </h3>
                {bankDetails && (
                  <div className="space-y-1 text-sm">
                    <p><strong>IBAN:</strong> {bankDetails.iban}</p>
                    <p><strong>{language === 'it' ? 'Beneficiario' : 'Beneficiary'}:</strong> {bankDetails.beneficiary}</p>
                    <p><strong>{language === 'it' ? 'Causale' : 'Reference'}:</strong> {bankDetails.reference}</p>
                  </div>
                )}
                {transferType === 'standard' && (
                  <p className="text-xs text-white/60 mt-3">
                    {language === 'it' 
                      ? '⏳ Il tuo ordine verrà elaborato dopo la conferma del bonifico' 
                      : '⏳ Your order will be processed after bank transfer confirmation'}
                  </p>
                )}
                {transferType === 'instant' && (
                  <p className="text-xs text-primary mt-3">
                    {language === 'it' 
                      ? '✅ Bonifico istantaneo confermato. Ordine in elaborazione!' 
                      : '✅ Instant transfer confirmed. Order processing!'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white/5 rounded-lg p-6 border border-white/10 mb-8">
          <h3 className="text-xl font-bold mb-4">
            {language === 'it' ? 'Prossimi Passi' : 'Next Steps'}
          </h3>
          <ul className="space-y-3 text-white/80">
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">1.</span>
              <span>
                {language === 'it' 
                  ? 'Riceverai una email di conferma con i dettagli dell\'ordine' 
                  : 'You will receive a confirmation email with order details'}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">2.</span>
              <span>
                {language === 'it' 
                  ? 'Il tuo ordine verrà preparato e spedito entro 24-48 ore' 
                  : 'Your order will be prepared and shipped within 24-48 hours'}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">3.</span>
              <span>
                {language === 'it' 
                  ? 'Riceverai un codice di tracciamento per seguire la spedizione' 
                  : 'You will receive a tracking code to follow the shipment'}
              </span>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={() => navigate('/products')}
            className="btn-primary"
          >
            {language === 'it' ? 'Continua lo Shopping' : 'Continue Shopping'}
          </button>
          <button 
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            {language === 'it' ? 'Torna alla Home' : 'Back to Home'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
